import { useState, useEffect, useRef, useCallback } from "react";
import { Send, Plus, Trash2, Lock, ChevronDown, BookOpen, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const tosImages = [
  "/images/tos/tos-bike.png",
  "/images/tos/tos-back.png",
  "/images/tos/tos-face.png",
];

const TosPage = () => {
  const navigate = useNavigate();
  const { isAdmin, getToken } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [knowledge, setKnowledge] = useState([]);
  const [newKnowledge, setNewKnowledge] = useState("");
  const [knowledgeCategory, setKnowledgeCategory] = useState("general");
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getAuthHeaders = useCallback(() => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  const fetchSessions = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tos/sessions`, { headers: getAuthHeaders() });
      setSessions(res.data);
    } catch {}
  }, [getAuthHeaders]);

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/tos/history/${sid}`, { headers: getAuthHeaders() });
      setMessages(res.data);
      setSessionId(sid);
      setShowSessions(false);
    } catch { toast.error("Kunne ikke hente samtale"); }
  };

  const fetchKnowledge = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tos/knowledge`, { headers: getAuthHeaders() });
      setKnowledge(res.data);
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSessions();
    if (isAdmin) fetchKnowledge();
  }, [fetchSessions, fetchKnowledge, isAdmin]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setSending(true);

    try {
      const res = await axios.post(`${API}/tos/chat`, 
        { message: text, session_id: sessionId },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
      if (res.data.knowledge_saved) toast.success("TOS har gemt din viden!", { duration: 2000 });
    } catch (err) {
      toast.error("TOS kunne ikke svare. Prøv igen.");
      setMessages(prev => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId(null);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const addKnowledgeEntry = async () => {
    if (!newKnowledge.trim()) return;
    try {
      await axios.post(`${API}/tos/knowledge`, 
        { content: newKnowledge, category: knowledgeCategory },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      toast.success("Viden tilføjet til TOS!");
      setNewKnowledge("");
      fetchKnowledge();
    } catch { toast.error("Kunne ikke tilføje viden"); }
  };

  const deleteKnowledge = async (id) => {
    try {
      await axios.delete(`${API}/tos/knowledge/${id}`, { headers: getAuthHeaders() });
      fetchKnowledge();
    } catch { toast.error("Kunne ikke slette"); }
  };

  const startDemo = async () => {
    startNewChat();
    const demoMsg = "Hej TOS! Vis mig hvad du kan — dette er en demo!";
    setMessages([{ role: "user", content: demoMsg }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/tos/chat`,
        { message: demoMsg, session_id: null },
        { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } }
      );
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      toast.error("TOS kunne ikke svare. Prøv igen.");
      setMessages([]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const tosAvatar = tosImages[2]; // face image

  return (
    <div className="fixed inset-0 pt-16 sm:pt-20 flex flex-col"
      style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(30,30,30,1) 0%, rgba(10,10,10,1) 60%)" }}>
      
      <div className="max-w-3xl mx-auto w-full flex-1 flex flex-col overflow-hidden px-2 sm:px-4 pb-2">
        {/* Header - compact on mobile */}
        <div className="flex items-center gap-2 sm:gap-3 py-2 sm:py-3 shrink-0">
          <button onClick={() => navigate(-1)} className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-white border border-gray-800 hover:border-gray-600 transition-all shrink-0" data-testid="back-btn" title="Tilbage">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <img src={tosAvatar} alt="TOS" className="w-9 h-9 sm:w-12 sm:h-12 rounded-full border-2 border-amber-500/40 object-cover shrink-0" />
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-xl font-['Oswald'] font-bold text-white tracking-wider flex items-center gap-1.5" data-testid="tos-title">
              TOS
              <span className="hidden sm:inline text-xs text-amber-400 font-normal px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20"><Lock className="w-3 h-3 inline" /> Kun medlemmer</span>
              <Lock className="sm:hidden w-3 h-3 text-amber-400" />
            </h1>
            <p className="text-gray-500 text-[10px] sm:text-xs truncate">The Others Spirit — din MC-assistent</p>
          </div>
          <div className="flex gap-1.5 sm:gap-2 shrink-0">
            <button onClick={() => setShowSessions(!showSessions)} 
              className="p-1.5 sm:p-2 rounded-lg text-gray-400 hover:text-white border border-gray-800 hover:border-gray-600 transition-all text-xs"
              data-testid="tos-sessions-btn" title="Tidligere samtaler">
              <ChevronDown className="w-4 h-4" />
            </button>
            <button onClick={startNewChat} 
              className="p-1.5 sm:p-2 rounded-lg text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
              data-testid="tos-new-chat-btn" title="Ny samtale">
              <Plus className="w-4 h-4" />
            </button>
            {isAdmin && (
              <button onClick={() => { setShowKnowledge(!showKnowledge); if (!showKnowledge) fetchKnowledge(); }}
                className="p-1.5 sm:p-2 rounded-lg text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-500/50 transition-all"
                data-testid="tos-knowledge-btn" title="Vidensbase (DD)">
                <BookOpen className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Sessions dropdown */}
        {showSessions && (
          <div className="mb-2 bg-black/60 backdrop-blur-sm border border-white/10 rounded-xl p-2 sm:p-3 max-h-36 overflow-y-auto shrink-0" data-testid="tos-sessions-list">
            {sessions.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-2">Ingen tidligere samtaler</p>
            ) : sessions.map(s => (
              <button key={s.session_id} onClick={() => loadSession(s.session_id)}
                className={`w-full text-left p-2 rounded-lg text-xs sm:text-sm mb-1 transition-all ${sessionId === s.session_id ? 'bg-amber-500/10 text-amber-400' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}>
                <span className="truncate block">{s.last_message}</span>
              </button>
            ))}
          </div>
        )}

        {/* Knowledge panel (admin only) */}
        {showKnowledge && isAdmin && (
          <div className="mb-2 bg-black/60 backdrop-blur-sm border border-amber-500/20 rounded-xl p-3 sm:p-4 shrink-0" data-testid="tos-knowledge-panel">
            <h3 className="text-amber-400 font-['Oswald'] font-bold text-xs sm:text-sm mb-2 tracking-wider">VIDENSBASE — Tilføj viden til TOS</h3>
            <div className="flex gap-1.5 sm:gap-2 mb-2">
              <select value={knowledgeCategory} onChange={e => setKnowledgeCategory(e.target.value)}
                className="bg-black/60 border border-gray-700 text-white text-xs rounded px-1.5 sm:px-2 py-1.5 shrink-0">
                <option value="general">Generelt</option>
                <option value="historie">Klubhistorie</option>
                <option value="regler">Regler</option>
                <option value="medlemmer">Medlemmer</option>
                <option value="events">Events</option>
                <option value="motorcykler">Motorcykler</option>
              </select>
              <input value={newKnowledge} onChange={e => setNewKnowledge(e.target.value)}
                onKeyDown={e => e.key === "Enter" && addKnowledgeEntry()}
                placeholder="Skriv viden TOS skal huske..."
                className="flex-1 min-w-0 bg-black/60 border border-gray-700 text-white text-xs sm:text-sm rounded px-2 sm:px-3 py-1.5 focus:border-amber-500/50 focus:outline-none"
                data-testid="tos-knowledge-input" />
              <button onClick={addKnowledgeEntry} className="px-2 sm:px-3 py-1.5 rounded bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 text-xs font-medium transition-all shrink-0"
                data-testid="tos-knowledge-add-btn">Tilføj</button>
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {knowledge.map(k => (
                <div key={k.knowledge_id} className="flex items-start gap-1.5 p-1.5 sm:p-2 rounded bg-black/30 border border-white/5">
                  <span className="text-[9px] sm:text-[10px] px-1 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20 shrink-0">{k.category}</span>
                  <span className="text-gray-300 text-xs flex-1 min-w-0 break-words">{k.content}</span>
                  <button onClick={() => deleteKnowledge(k.knowledge_id)} className="text-red-500 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Chat area */}
        <div className="flex-1 bg-black/30 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4" data-testid="tos-chat-area">
            {messages.length === 0 && !sending && (
              <div className="flex flex-col items-center justify-center h-full text-center py-8 sm:py-12">
                <img src={tosAvatar} alt="TOS" className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-amber-500/30 object-cover mb-3 sm:mb-4 opacity-60" />
                <p className="text-gray-400 font-['Oswald'] text-base sm:text-lg mb-1">Hej makker!</p>
                <p className="text-gray-600 text-xs sm:text-sm max-w-xs mb-4 sm:mb-5 px-4">Skriv en besked og lad os snakke MC, klubben eller hvad du har på hjertet.</p>
                <button onClick={startDemo}
                  className="px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl font-['Oswald'] font-bold text-sm tracking-wider uppercase bg-gradient-to-r from-amber-600/30 to-amber-500/10 text-amber-400 border border-amber-500/40 hover:border-amber-400 hover:from-amber-600/40 hover:to-amber-500/20 transition-all shadow-lg"
                  style={{ boxShadow: "0 0 20px rgba(212,175,55,0.15)" }}
                  data-testid="tos-demo-btn">
                  Prøv mig
                </button>
                <p className="text-gray-600 text-xs mt-2">Demofunktion</p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 sm:gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`} data-testid={`tos-msg-${i}`}>
                {msg.role === "assistant" && (
                  <img src={tosAvatar} alt="TOS" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-amber-500/30 object-cover shrink-0 mt-1" />
                )}
                <div className={`max-w-[85%] sm:max-w-[80%] rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 text-[13px] sm:text-sm leading-relaxed whitespace-pre-wrap ${
                  msg.role === "user"
                    ? "bg-blue-600/20 text-white border border-blue-500/20 rounded-tr-sm"
                    : "bg-white/5 text-gray-200 border border-white/5 rounded-tl-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex gap-2 sm:gap-3">
                <img src={tosAvatar} alt="TOS" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border border-amber-500/30 object-cover shrink-0 mt-1" />
                <div className="bg-white/5 border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input - sticky bottom */}
          <div className="p-2 sm:p-3 border-t border-white/10 bg-black/40 shrink-0">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
                placeholder="Skriv til TOS..."
                disabled={sending}
                className="flex-1 bg-black/40 border border-gray-700 text-white text-sm rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 focus:border-amber-500/50 focus:outline-none disabled:opacity-50 placeholder-gray-600"
                data-testid="tos-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={sending || !input.trim()}
                className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all shrink-0"
                data-testid="tos-send-btn"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TosPage;
